// src/components/admin/blog/SEOScoreHelper.jsx
import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';

export const SEOScoreHelper = ({ title, description, keywords }) => {
  const [score, setScore] = useState(0);
  const [checks, setChecks] = useState([]);

  useEffect(() => {
    const newChecks = [];
    let totalScore = 0;

    // Title check
    if (title) {
      if (title.length >= 30 && title.length <= 60) {
        newChecks.push({
          name: 'Title Length',
          status: 'success',
          message: 'Good length (30-60 chars)',
        });
        totalScore += 20;
      } else if (title.length > 60) {
        newChecks.push({
          name: 'Title Length',
          status: 'warning',
          message: 'Too long (>60 chars)',
        });
        totalScore += 10;
      } else if (title.length < 30) {
        newChecks.push({
          name: 'Title Length',
          status: 'warning',
          message: 'Too short (<30 chars)',
        });
        totalScore += 10;
      }
    } else {
      newChecks.push({
        name: 'Title',
        status: 'error',
        message: 'Missing SEO title',
      });
    }

    // Description check
    if (description) {
      if (description.length >= 120 && description.length <= 160) {
        newChecks.push({
          name: 'Description Length',
          status: 'success',
          message: 'Good length (120-160 chars)',
        });
        totalScore += 20;
      } else if (description.length > 160) {
        newChecks.push({
          name: 'Description Length',
          status: 'warning',
          message: 'Too long (>160 chars)',
        });
        totalScore += 10;
      } else if (description.length < 120) {
        newChecks.push({
          name: 'Description Length',
          status: 'warning',
          message: 'Too short (<120 chars)',
        });
        totalScore += 10;
      }
    } else {
      newChecks.push({
        name: 'Description',
        status: 'error',
        message: 'Missing meta description',
      });
    }

    // Keywords check
    if (keywords && keywords.length > 0) {
      newChecks.push({
        name: 'Keywords',
        status: 'success',
        message: `${keywords.length} keywords added`,
      });
      totalScore += 15;
    } else {
      newChecks.push({
        name: 'Keywords',
        status: 'warning',
        message: 'No keywords added',
      });
    }

    // Title includes main keyword
    if (title && keywords && keywords.length > 0) {
      const mainKeyword = keywords[0];
      if (title.toLowerCase().includes(mainKeyword.toLowerCase())) {
        newChecks.push({
          name: 'Keyword in Title',
          status: 'success',
          message: 'Main keyword in title',
        });
        totalScore += 15;
      } else {
        newChecks.push({
          name: 'Keyword in Title',
          status: 'warning',
          message: 'Main keyword missing from title',
        });
      }
    }

    // Description includes main keyword
    if (description && keywords && keywords.length > 0) {
      const mainKeyword = keywords[0];
      if (description.toLowerCase().includes(mainKeyword.toLowerCase())) {
        newChecks.push({
          name: 'Keyword in Description',
          status: 'success',
          message: 'Main keyword in description',
        });
        totalScore += 15;
      } else {
        newChecks.push({
          name: 'Keyword in Description',
          status: 'warning',
          message: 'Main keyword missing from description',
        });
      }
    }

    setChecks(newChecks);
    setScore(Math.min(100, totalScore));
  }, [title, description, keywords]);

  const getScoreColor = () => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBackground = () => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'success':
        return <FiCheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning':
        return <FiAlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'error':
        return <FiAlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FiInfo className="w-4 h-4" />;
    }
  };

  return (
    <div
      className="p-4 rounded-xl border"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold" style={{ color: 'var(--text)' }}>
          SEO Score
        </h4>
        <div className="flex items-center gap-2">
          <span className={`text-2xl font-bold ${getScoreColor()}`}>
            {score}
          </span>
          <span className="text-sm" style={{ color: 'var(--muted)' }}>
            /100
          </span>
        </div>
      </div>

      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full mb-4 overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${getScoreBackground()}`}
          style={{ width: `${score}%` }}
        />
      </div>

      <div className="space-y-2">
        {checks.map((check, index) => (
          <div key={index} className="flex items-start gap-2 text-sm">
            {getStatusIcon(check.status)}
            <div className="flex-1">
              <span className="font-medium" style={{ color: 'var(--text)' }}>
                {check.name}:
              </span>
              <span className="ml-1" style={{ color: 'var(--muted)' }}>
                {check.message}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-3 pt-3 border-t text-xs"
        style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
      >
        <FiInfo className="inline w-3 h-3 mr-1" />
        Good SEO practices can improve search engine rankings
      </div>
    </div>
  );
};
